// Copyright 2025, University of Maryland, All Rights Reserved
import { useQuery } from '@tanstack/react-query';
import { fetchUserProfile } from '../services/users-api';
import type { UserProfileResponse } from '../types/user';

/**
 * Standardized query key for user profile data.
 * Using a consistent key ensures all components share the same cached data.
 */
export const USER_PROFILE_QUERY_KEY = ['user-profile'];

/**
 * React Query hook for fetching the current user's profile.
 * Uses shared query cache to prevent duplicate API calls.
 */
export function useUserProfile(enabled = true) {
  return useQuery<UserProfileResponse>({
    queryKey: USER_PROFILE_QUERY_KEY,
    queryFn: fetchUserProfile,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true, // Refetch to catch impersonation changes
  });
}
